/**
 * `W5-T18` §4.8 — where the professional's share of a started call-out stands, read for the work page.
 *
 * Loader-first, as every read here is (ADR-011 R3): the work page's loader calls this once the client
 * approved, and `CalloutReleaseStatus` renders what it answered. Fresh on every run, because the run
 * *is* the re-read: the page's own poll and the component's watch both work by re-running loaders.
 *
 * `null` is "say nothing": a read that failed leaves the approval line on its own, and never turns the
 * page into an error. The API answers the same `404` to anyone who is not the booking's professional,
 * and that is nothing to show either.
 */
import { type CalloutReleaseView } from '@marketplace/contracts';
import { type LoaderFunctionArgs } from 'react-router';
import { queryKeys, routeContext } from '../../shared/query.js';

/** While the transfer is being sent, re-read every three seconds, for a minute (§4.8). */
export const RELEASE_WATCH_EVERY_MS = 3_000;
export const RELEASE_WATCH_FOR_MS = 60_000;

export async function loadCalloutRelease(
  context: LoaderFunctionArgs['context'],
  jobId: string,
): Promise<CalloutReleaseView | null> {
  const { queryClient, api } = context.get(routeContext);
  return queryClient
    .fetchQuery({
      queryKey: queryKeys.calloutRelease(jobId),
      queryFn: () => api.getCalloutRelease(jobId),
      staleTime: 0,
    })
    .catch(() => null);
}
